// SE（効果音）再生サービス。WebAudio Oscillator で合成する。
import { getAudioContext, resumeAudioContext } from './audioContext.ts'
import {
  SE_DEFINITIONS,
  SE_DEBOUNCE_MS,
  type OscillatorDef,
  type SeId,
} from '../config/se.ts'
import { isSeEffectivelyMuted } from './volume.ts'

const lastPlayedAt = new Map<SeId, number>()

export function playSe(id: SeId): void {
  // ユーザー設定 or 広告ダッキング中は発音しない（不要な AudioNode 生成も避ける）。
  if (isSeEffectivelyMuted()) return

  // 連続呼び出し吸収（StrictMode の二重発火やダブルクリック対策）
  const now = performance.now()
  const last = lastPlayedAt.get(id) ?? 0
  if (now - last < SE_DEBOUNCE_MS) return
  lastPlayedAt.set(id, now)

  resumeAudioContext()
  const ctx = getAudioContext()
  if (!ctx) return

  const def = SE_DEFINITIONS[id]
  for (const osc of def.oscillators) {
    playOscillator(ctx, osc, def.masterVolume)
  }
}

function playOscillator(
  ctx: AudioContext,
  osc: OscillatorDef,
  masterVolume: number
): void {
  const startAt = ctx.currentTime + (osc.delay ?? 0)
  const duration = osc.attack + osc.decay + osc.release
  const endAt = startAt + duration

  let oscillator: OscillatorNode
  let gain: GainNode
  try {
    oscillator = ctx.createOscillator()
    gain = ctx.createGain()
  } catch {
    // fallback:ok — ノード生成失敗は単発 SE のみスキップ
    return
  }

  oscillator.type = osc.type
  oscillator.frequency.setValueAtTime(osc.frequency, startAt)
  if (osc.endFrequency !== undefined) {
    // exponentialRampToValueAtTime は 0 では throw するため 0.01 で clamp
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(osc.endFrequency, 0.01),
      endAt
    )
  }

  const peak = osc.volume * masterVolume
  const sustainLevel = Math.max(osc.sustain * peak, 0.0001)

  gain.gain.setValueAtTime(0.0001, startAt)
  gain.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0001), startAt + osc.attack)
  if (osc.decay > 0) {
    gain.gain.exponentialRampToValueAtTime(
      sustainLevel,
      startAt + osc.attack + osc.decay
    )
  }
  gain.gain.exponentialRampToValueAtTime(0.0001, endAt)

  oscillator.connect(gain)
  gain.connect(ctx.destination)

  try {
    oscillator.start(startAt)
    oscillator.stop(endAt + 0.02)
  } catch {
    // fallback:ok — start/stop 失敗は単発 SE のみスキップ
  }
}
